import React, { useState, useRef } from 'react';
import { 
  ArrowLeft, Upload, Camera, Search, TrendingUp, TrendingDown, 
  Target, AlertTriangle, Shield, BarChart2, Clock, ChevronDown,
  X, Loader2, CheckCircle, Info, Zap, ArrowUpCircle, ArrowDownCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// API URL helper
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

// Market types
const MARKET_TYPES = [
  { value: 'forex', label: 'FOREX' },
  { value: 'otc', label: 'OTC' },
];

// Common trading pairs
const FOREX_PAIRS = [
  'EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/USD', 'USD/CAD', 
  'EUR/GBP', 'EUR/JPY', 'GBP/JPY', 'AUD/JPY', 'NZD/USD',
  'USD/CHF', 'EUR/CHF', 'GBP/CHF', 'CAD/JPY', 'AUD/CAD',
  'XAU/USD', 'US30'
];

const OTC_PAIRS = [
  'EUR/USD OTC', 'GBP/USD OTC', 'USD/JPY OTC', 'AUD/USD OTC', 
  'USD/CAD OTC', 'EUR/GBP OTC', 'EUR/JPY OTC', 'GBP/JPY OTC',
  'AUD/JPY OTC', 'NZD/USD OTC', 'USD/CHF OTC', 'CAD/JPY OTC',
  'AUD/CAD OTC', 'EUR/NZD OTC', 'USD/BRL OTC', 'GOLD OTC'
];

// Timeframes for each chart
const TIMEFRAME_OPTIONS = [
  { value: '1', label: '1m' },
  { value: '2', label: '2m' },
  { value: '3', label: '3m' },
  { value: '5', label: '5m' },
  { value: '15', label: '15m' },
  { value: '30', label: '30m' },
  { value: '60', label: '1H' },
  { value: '240', label: '4H' },
  { value: '1440', label: 'D1' },
];

const ChartAnalyzer = ({ user, onBack }) => {
  // Form state
  const [marketType, setMarketType] = useState('otc');
  const [selectedPair, setSelectedPair] = useState('');
  const [showPairDropdown, setShowPairDropdown] = useState(false);
  const [tradingStyle, setTradingStyle] = useState('day'); // scalp, day, swing
  
  // Multi-timeframe image state - 3 charts
  const [charts, setCharts] = useState([
    { id: 1, label: 'Higher Timeframe', timeframe: '60', preview: null, base64: null },
    { id: 2, label: 'Middle Timeframe', timeframe: '15', preview: null, base64: null },
    { id: 3, label: 'Entry Timeframe', timeframe: '5', preview: null, base64: null },
  ]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [tradeReported, setTradeReported] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  
  // Refs for file inputs
  const fileInputRefs = useRef([null, null, null]);
  
  // Get pairs based on market type
  const getPairs = () => {
    return marketType === 'forex' ? FOREX_PAIRS : OTC_PAIRS;
  };
  
  // Report trade result
  const reportTradeResult = async (result) => {
    if (!analysisId || tradeReported) return;
    
    setIsReporting(true);
    try {
      const response = await fetch(`${API}/chart/report-result`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('guestToken')}`
        },
        body: JSON.stringify({
          analysis_id: analysisId,
          result: result
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setTradeReported(true);
        toast.success(`Trade reported as ${result.toUpperCase()}!`);
      } else {
        toast.error(data.message || 'Failed to report trade');
      }
    } catch (error) {
      console.error('Report error:', error);
      toast.error('Failed to report trade result');
    } finally {
      setIsReporting(false);
    }
  };
  
  // Handle file upload for specific chart
  const handleFileUpload = (chartIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const newCharts = [...charts];
      newCharts[chartIndex].preview = e.target.result;
      newCharts[chartIndex].base64 = e.target.result.split(',')[1];
      setCharts(newCharts);
    };
    reader.readAsDataURL(file);
    setAnalysisResult(null);
  };
  
  // Clear image for specific chart
  const clearImage = (chartIndex) => {
    const newCharts = [...charts];
    newCharts[chartIndex].preview = null;
    newCharts[chartIndex].base64 = null;
    setCharts(newCharts);
    if (fileInputRefs.current[chartIndex]) {
      fileInputRefs.current[chartIndex].value = '';
    }
    setAnalysisResult(null);
  };
  
  // Update timeframe for specific chart
  const updateTimeframe = (chartIndex, timeframe) => {
    const newCharts = [...charts];
    newCharts[chartIndex].timeframe = timeframe;
    setCharts(newCharts);
    setActiveDropdown(null);
  };
  
  // Check if at least one chart is uploaded
  const hasAnyChart = () => charts.some(c => c.base64);
  
  // Get uploaded charts count
  const uploadedCount = () => charts.filter(c => c.base64).length;
  
  // Analyze charts
  const analyzeCharts = async () => {
    if (!selectedPair) {
      toast.error('Please select a trading pair');
      return;
    }
    
    if (!hasAnyChart()) {
      toast.error('Please upload at least one chart image');
      return;
    }
    
    if (!disclaimerAccepted) {
      toast.error('Please accept the disclaimer to continue');
      return;
    }
    
    setIsAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      // Prepare charts data - only send uploaded ones
      const chartsData = charts
        .filter(c => c.base64)
        .map(c => ({
          timeframe: c.timeframe,
          image_base64: c.base64
        }));
      
      const response = await fetch(`${API}/chart/analyze-mtf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('guestToken')}`
        },
        body: JSON.stringify({
          charts: chartsData,
          market_type: marketType,
          pair: selectedPair,
          trading_style: marketType === 'forex' ? tradingStyle : null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAnalysisResult(data.analysis);
        setAnalysisId(data.analysis_id);
        setTradeReported(false);
        toast.success(uploadedCount() === 1 ? 'Chart analysis complete!' : 'Multi-timeframe analysis complete!');
      } else {
        toast.error(data.message || 'Analysis failed. Please try again.');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Connection error. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  // Get direction color
  const getDirectionColor = (direction) => {
    if (!direction) return 'text-gray-500';
    const d = direction.toLowerCase();
    if (d.includes('bull') || d.includes('buy') || d.includes('up')) return 'text-green-500';
    if (d.includes('bear') || d.includes('sell') || d.includes('down')) return 'text-red-500';
    return 'text-yellow-500';
  };
  
  // Get direction icon
  const getDirectionIcon = (direction) => {
    if (!direction) return <Target className="w-6 h-6" />;
    const d = direction.toLowerCase();
    if (d.includes('bull') || d.includes('buy') || d.includes('up')) {
      return <ArrowUpCircle className="w-6 h-6 text-green-500" />;
    }
    if (d.includes('bear') || d.includes('sell') || d.includes('down')) {
      return <ArrowDownCircle className="w-6 h-6 text-red-500" />;
    }
    return <Target className="w-6 h-6 text-yellow-500" />;
  };
  
  // Get agreement color
  const getAgreementColor = (level) => {
    if (!level) return 'text-gray-400';
    const l = level.toLowerCase();
    if (l.includes('strong') || l.includes('high') || l.includes('full')) return 'text-green-400';
    if (l.includes('partial') || l.includes('moderate') || l.includes('medium')) return 'text-yellow-400';
    return 'text-red-400';
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-white/10 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <BarChart2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg text-white">MTF Chart Analyzer</span>
              <span className="text-xs bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-2 py-0.5 rounded-full">PRO</span>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-5xl mx-auto px-4 py-4 space-y-4">
        {/* Disclaimer Card */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-amber-200/80 text-xs mb-2">
                <strong>Disclaimer:</strong> This AI analysis is for <strong>educational purposes only</strong>. NOT financial advice. Trading involves significant risk.
              </p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={disclaimerAccepted}
                  onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                  className="w-4 h-4 rounded border-amber-500/50 bg-amber-500/20 text-amber-500 focus:ring-amber-500"
                />
                <span className="text-amber-200 text-sm font-medium">I understand this is NOT financial advice</span>
              </label>
            </div>
          </div>
        </div>
        
        {/* Market & Pair Selection */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-3 relative z-10">
          <div className={`grid gap-3 ${marketType === 'forex' ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {/* Market Type */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Market Type</label>
              <div className="flex gap-2">
                {MARKET_TYPES.map(type => (
                  <button
                    key={type.value}
                    onClick={() => { setMarketType(type.value); setSelectedPair(''); }}
                    className={`flex-1 px-3 py-1.5 rounded-lg font-medium text-sm transition-all ${
                      marketType === type.value ? 'bg-purple-600 text-white' : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Trading Pair */}
            <div className="relative">
              <label className="block text-xs text-gray-400 mb-1.5">Trading Pair</label>
              <button
                onClick={() => setShowPairDropdown(!showPairDropdown)}
                className="w-full px-3 py-1.5 bg-slate-700 border border-white/10 rounded-lg text-left text-white flex items-center justify-between hover:bg-slate-600 transition-colors text-sm"
              >
                <span className={selectedPair ? 'text-white' : 'text-gray-400'}>{selectedPair || 'Select pair...'}</span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showPairDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showPairDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-white/10 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                  {getPairs().map(pair => (
                    <button
                      key={pair}
                      onClick={() => { setSelectedPair(pair); setShowPairDropdown(false); }}
                      className={`w-full px-3 py-1.5 text-left text-sm hover:bg-slate-700 ${selectedPair === pair ? 'bg-purple-600/30 text-purple-300' : 'text-gray-300'}`}
                    >
                      {pair}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Trading Style - Only for FOREX */}
            {marketType === 'forex' && (
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Trading Style</label>
                <div className="flex gap-1">
                  {[
                    { value: 'scalp', label: 'Scalp', desc: '1-15 min' },
                    { value: 'day', label: 'Day', desc: '15min-4H' },
                    { value: 'swing', label: 'Swing', desc: '4H-Days' }
                  ].map(style => (
                    <button
                      key={style.value}
                      onClick={() => setTradingStyle(style.value)}
                      className={`flex-1 px-2 py-1.5 rounded-lg text-xs transition-all ${
                        tradingStyle === style.value 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                      }`}
                    >
                      <div className="font-medium">{style.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Multi-Timeframe Charts Upload */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
              <BarChart2 className="w-4 h-4 text-blue-400" />
              Upload Chart(s)
            </h3>
            <span className="text-xs text-gray-400 bg-slate-700 px-2 py-1 rounded">
              {uploadedCount() === 0 ? 'Upload at least 1' : `${uploadedCount()}/3 uploaded`}
            </span>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            {charts.map((chart, index) => (
              <div key={chart.id} className="relative">
                {/* Timeframe selector */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-400">{index === 0 ? 'Chart 1' : index === 1 ? 'Chart 2 (optional)' : 'Chart 3 (optional)'}</span>
                  <div className="relative">
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === index ? null : index)}
                      className="px-2 py-1 bg-slate-700 rounded text-xs text-white flex items-center gap-1 hover:bg-slate-600"
                    >
                      {TIMEFRAME_OPTIONS.find(t => t.value === chart.timeframe)?.label}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    {activeDropdown === index && (
                      <div className="absolute top-full right-0 mt-1 bg-slate-800 border border-white/10 rounded shadow-xl z-50 min-w-[60px]">
                        {TIMEFRAME_OPTIONS.map(tf => (
                          <button
                            key={tf.value}
                            onClick={() => updateTimeframe(index, tf.value)}
                            className={`w-full px-2 py-1 text-left text-xs hover:bg-slate-700 ${chart.timeframe === tf.value ? 'bg-purple-600/30 text-purple-300' : 'text-gray-300'}`}
                          >
                            {tf.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Upload area */}
                {!chart.preview ? (
                  <div 
                    onClick={() => fileInputRefs.current[index]?.click()}
                    className="border border-dashed border-white/20 rounded-lg p-4 text-center cursor-pointer hover:border-purple-500/50 hover:bg-purple-500/5 transition-all min-h-[100px] flex flex-col items-center justify-center"
                  >
                    <Upload className="w-6 h-6 text-gray-500 mb-1" />
                    <p className="text-xs text-gray-400">Click to upload</p>
                    <input
                      type="file"
                      ref={el => fileInputRefs.current[index] = el}
                      onChange={(e) => handleFileUpload(index, e)}
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="relative">
                    <img
                      src={chart.preview}
                      alt={`${chart.label} chart`}
                      className="w-full h-[100px] object-cover rounded-lg border border-white/10"
                    />
                    <button
                      onClick={() => clearImage(index)}
                      className="absolute top-1 right-1 p-1 bg-red-500/80 hover:bg-red-500 rounded-full text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 rounded text-xs text-white">
                      {TIMEFRAME_OPTIONS.find(t => t.value === chart.timeframe)?.label}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <p className="text-xs text-gray-500 mt-2 text-center">
            📊 Upload 1 chart for single analysis, or up to 3 for multi-timeframe confluence
          </p>
        </div>
        
        {/* Analyze Button */}
        <Button
          onClick={analyzeCharts}
          disabled={!hasAnyChart() || !selectedPair || !disclaimerAccepted || isAnalyzing}
          className="w-full py-5 text-base font-bold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50"
        >
          {isAnalyzing ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analyzing {uploadedCount()} Chart(s)...</>
          ) : (
            <><Search className="w-5 h-5 mr-2" /> {uploadedCount() === 1 ? 'Analyze Chart' : `Analyze ${uploadedCount()} Charts`}</>
          )}
        </Button>
        
        {/* Analysis Results */}
        {analysisResult && (
          <div className="space-y-4 animate-fadeIn">
            {/* MTF Agreement Card */}
            <div className={`bg-slate-800/50 backdrop-blur-sm border rounded-xl p-4 ${
              analysisResult.mtf_agreement?.toLowerCase().includes('strong') ? 'border-green-500/30' :
              analysisResult.mtf_agreement?.toLowerCase().includes('partial') ? 'border-yellow-500/30' : 'border-red-500/30'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-400" />
                  Multi-Timeframe Agreement
                </h3>
                <span className={`font-bold text-lg ${getAgreementColor(analysisResult.mtf_agreement)}`}>
                  {analysisResult.mtf_agreement || 'N/A'}
                </span>
              </div>
              {analysisResult.mtf_summary && (
                <p className="text-gray-300 text-sm">{analysisResult.mtf_summary}</p>
              )}
            </div>
            
            {/* Entry Suggestion Card */}
            <div className={`bg-slate-800/50 backdrop-blur-sm border rounded-xl p-4 ${
              analysisResult.entry_direction?.toLowerCase().includes('buy') ? 'border-green-500/30' :
              analysisResult.entry_direction?.toLowerCase().includes('sell') ? 'border-red-500/30' : 'border-yellow-500/30'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Entry Recommendation
                </h3>
                <div className="flex items-center gap-2">
                  {getDirectionIcon(analysisResult.entry_direction)}
                  <span className={`font-bold text-xl ${getDirectionColor(analysisResult.entry_direction)}`}>
                    {analysisResult.entry_direction || 'NEUTRAL'}
                  </span>
                </div>
              </div>
              
              {/* Key Entry Level */}
              <div className={`rounded-lg p-3 mb-3 ${
                analysisResult.entry_direction?.toLowerCase().includes('buy') ? 'bg-green-500/20 border border-green-500/30' :
                analysisResult.entry_direction?.toLowerCase().includes('sell') ? 'bg-red-500/20 border border-red-500/30' : 'bg-yellow-500/20 border border-yellow-500/30'
              }`}>
                <p className="text-xs text-gray-400 mb-1">Key Entry Level</p>
                <p className={`font-bold text-lg ${getDirectionColor(analysisResult.entry_direction)}`}>
                  {analysisResult.key_entry_level || 'Current Price'}
                </p>
              </div>
              
              {/* FOREX: Stop Loss & Take Profit | OTC: Expiry */}
              {marketType === 'forex' ? (
                <>
                  {/* Trading Style Badge */}
                  {analysisResult.trading_style && (
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-xs text-gray-400">Trading Style:</span>
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs font-medium capitalize">
                        {analysisResult.trading_style} Trader
                      </span>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-1">Stop Loss</p>
                      <p className="font-bold text-red-400">{analysisResult.stop_loss || 'N/A'}</p>
                    </div>
                    <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-1">Take Profit</p>
                      <p className="font-bold text-green-400">{analysisResult.take_profit || 'N/A'}</p>
                    </div>
                    <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-1">Risk:Reward</p>
                      <p className="font-bold text-blue-400">{analysisResult.risk_reward_ratio || 'N/A'}</p>
                    </div>
                  </div>
                  {/* SL/TP Reasoning */}
                  {analysisResult.sl_tp_reasoning && (
                    <div className="bg-slate-700/50 rounded-lg p-3 mb-3">
                      <h4 className="text-cyan-300 font-semibold mb-2 text-sm flex items-center gap-2">
                        <Shield className="w-4 h-4" /> Why These SL/TP Levels?
                      </h4>
                      <p className="text-gray-300 text-sm leading-relaxed">{analysisResult.sl_tp_reasoning}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-3 mb-3">
                  <p className="text-xs text-gray-400 mb-1">Recommended Expiry</p>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-400" />
                    <p className="font-bold text-lg text-purple-300">{analysisResult.expiry_recommendation || 'N/A'}</p>
                  </div>
                </div>
              )}
              
              {/* Confidence */}
              {analysisResult.confidence && (
                <div className="mb-3">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-400">Confidence Level</span>
                    <span className="text-white font-medium">{analysisResult.confidence}%</span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div className={`h-2 rounded-full ${analysisResult.confidence >= 70 ? 'bg-green-500' : analysisResult.confidence >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${analysisResult.confidence}%` }} />
                  </div>
                </div>
              )}
              
              {/* Reasoning */}
              {analysisResult.entry_reasoning && (
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <h4 className="text-purple-300 font-semibold mb-2 text-sm flex items-center gap-2">
                    <Info className="w-4 h-4" /> Analysis & Reasoning
                  </h4>
                  <p className="text-gray-300 text-sm leading-relaxed">{analysisResult.entry_reasoning}</p>
                </div>
              )}
            </div>
            
            {/* Timeframe Breakdown */}
            {analysisResult.timeframe_analysis && analysisResult.timeframe_analysis.length > 0 && (
              <div className="bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
                  <BarChart2 className="w-4 h-4 text-blue-400" />
                  Timeframe Breakdown
                </h3>
                <div className="space-y-2">
                  {analysisResult.timeframe_analysis.map((tf, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-700/50 rounded-lg px-3 py-2">
                      <span className="text-gray-300 text-sm">{tf.timeframe}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${getDirectionColor(tf.bias)}`}>{tf.bias}</span>
                        {tf.bias?.toLowerCase().includes('bull') || tf.bias?.toLowerCase().includes('buy') ? (
                          <TrendingUp className="w-4 h-4 text-green-500" />
                        ) : tf.bias?.toLowerCase().includes('bear') || tf.bias?.toLowerCase().includes('sell') ? (
                          <TrendingDown className="w-4 h-4 text-red-500" />
                        ) : (
                          <Target className="w-4 h-4 text-yellow-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Support & Resistance */}
            {(analysisResult.support_levels?.length > 0 || analysisResult.resistance_levels?.length > 0) && (
              <div className="bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
                  <Shield className="w-4 h-4 text-cyan-400" /> Key Levels
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {analysisResult.resistance_levels?.length > 0 && (
                    <div>
                      <p className="text-red-400 text-xs font-medium mb-1">Resistance</p>
                      <div className="space-y-1">
                        {analysisResult.resistance_levels.map((level, idx) => (
                          <p key={idx} className="text-gray-300 text-sm bg-red-500/10 px-2 py-1 rounded">{level}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  {analysisResult.support_levels?.length > 0 && (
                    <div>
                      <p className="text-green-400 text-xs font-medium mb-1">Support</p>
                      <div className="space-y-1">
                        {analysisResult.support_levels.map((level, idx) => (
                          <p key={idx} className="text-gray-300 text-sm bg-green-500/10 px-2 py-1 rounded">{level}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Patterns */}
            {analysisResult.patterns?.length > 0 && (
              <div className="bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Target className="w-4 h-4 text-purple-400" /> Patterns Identified
                </h3>
                <div className="flex flex-wrap gap-2">
                  {analysisResult.patterns.map((pattern, idx) => (
                    <span key={idx} className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs font-medium">{pattern}</span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Trade Result Reporting */}
            {analysisResult.entry_direction && analysisResult.entry_direction !== 'NO TRADE' && (
              <div className="bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-blue-400" />
                  Report Trade Result
                </h3>
                
                {!tradeReported ? (
                  <div className="flex gap-3">
                    <button
                      onClick={() => reportTradeResult('win')}
                      disabled={isReporting}
                      className="flex-1 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                      {isReporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                      WIN
                    </button>
                    <button
                      onClick={() => reportTradeResult('loss')}
                      disabled={isReporting}
                      className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                      {isReporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <X className="w-5 h-5" />}
                      LOSS
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-3 bg-slate-700/50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-1" />
                    <p className="text-green-400 font-medium text-sm">Trade result recorded!</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Disclaimer */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-2 text-center">
              <p className="text-red-300 text-xs">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                This is for educational purposes only - NOT financial advice.
              </p>
            </div>
          </div>
        )}
      </main>
      
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default ChartAnalyzer;
